<template>
  <div class="agent-dashboard-layout">
    <!-- Sidebar -->
    <Sidebar :isAgent="true" />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <!-- Welcome Section -->
        <div class="welcome-header">
          <div class="welcome-section">
            <h1>Welcome back, {{ agentName }}!</h1>
            <p class="subtitle">{{ departmentName }} • {{ roleName }}</p>
          </div>
          <button class="refresh-button" @click="refreshData" :disabled="isRefreshing">
            <svg :class="{ spinning: isRefreshing }" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
            </svg>
            Refresh
          </button>
        </div>

    <!-- Quick Actions -->
    <div class="quick-actions-section">
      <h2>Quick Actions</h2>
      <div class="actions-grid">
        <router-link to="/leads" class="action-card">
          <div class="action-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
            </svg>
          </div>
          <div class="action-content">
            <h3>Leads</h3>
            <p>View and manage leads</p>
          </div>
          <div class="action-arrow">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
            </svg>
          </div>
        </router-link>

        <router-link to="/whatsapp" class="action-card">
          <div class="action-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
            </svg>
          </div>
          <div class="action-content">
            <h3>WhatsApp</h3>
            <p>Handle customer conversations</p>
          </div>
          <div class="action-arrow">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
            </svg>
          </div>
        </router-link>

        <router-link to="/messages" class="action-card">
          <div class="action-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 9h12v2H6V9zm8 5H6v-2h8v2zm4-6H6V6h12v2z"/>
            </svg>
          </div>
          <div class="action-content">
            <h3>Team Messaging</h3>
            <p>Communicate with team members</p>
          </div>
          <div class="action-arrow">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
            </svg>
          </div>
        </router-link>
      </div>
    </div>

    <!-- Lead Intelligence Center Card -->
    <div class="insight-card lead-insights">
      <div class="insight-header">
        <div class="insight-title-with-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" class="lead-intelligence-icon">
            <path d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,12L13.41,16L19.71,9.71L22,12V6H16Z"/>
          </svg>
          <div class="insight-title-content">
            <h3 class="insight-title">Lead Intelligence Center</h3>
            <p class="insight-subtitle">Track and analyze your lead generation performance</p>
          </div>
        </div>
        <span class="insight-count" v-if="!leadData.loading">{{ leadData.todayStats?.count || 0 }} today</span>
      </div>

      <!-- Show loading state -->
      <div v-if="leadData.loading" class="insights-loading">
        <div class="loading-spinner"></div>
        <p>Loading...</p>
      </div>

      <!-- Show error state -->
      <div v-else-if="leadData.error" class="insights-error">
        <div class="error-icon">⚠️</div>
        <p>{{ leadData.error }}</p>
      </div>

      <!-- Show lead stats -->
      <div v-else class="lead-stats-grid">
        <div class="stat-card primary">
          <div class="stat-number">{{ leadData.todayStats?.count || 0 }}</div>
          <div class="stat-label">Today</div>
          <div class="stat-rate">{{ leadData.todayStats?.completion_rate?.toFixed(1) || 0 }}%</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ leadData.weekStats?.count || 0 }}</div>
          <div class="stat-label">Week</div>
          <div class="stat-rate">{{ leadData.weekStats?.completion_rate?.toFixed(1) || 0 }}%</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ leadData.monthStats?.count || 0 }}</div>
          <div class="stat-label">Month</div>
          <div class="stat-rate">{{ leadData.monthStats?.completion_rate?.toFixed(1) || 0 }}%</div>
        </div>
      </div>

      <!-- Recent activity -->
      <div class="recent-activity" v-if="leadData.recentCompletions?.length">
        <h4 class="activity-title">Recent Completions</h4>
        <div class="activity-list">
          <div class="activity-item" v-for="completion in leadData.recentCompletions.slice(0, 2)" :key="completion.phone_number">
            <div class="activity-dot success"></div>
            <div class="activity-info">
              <div class="activity-text">{{ completion.name }} - {{ completion.company_name }}</div>
              <div class="activity-time">{{ formatTime(completion.completed_at) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Motivational Message -->
      <div class="motivational-message" v-if="leadData.motivationalMessage">
        <div class="motivational-content">
          <span class="motivational-text">{{ leadData.motivationalMessage }}</span>
        </div>
      </div>
    </div>

    <!-- WhatsApp Connect Card -->
    <div class="insight-card whatsapp-insights">
      <div class="insight-header">
        <div class="insight-title-with-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" class="whatsapp-icon">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.570-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.525 3.487"/>
          </svg>
          <div class="insight-title-content">
            <h3 class="insight-title">WhatsApp Connect Insights</h3>
            <p class="insight-subtitle">Monitor your WhatsApp conversation activity</p>
          </div>
        </div>
        <span class="insight-count" v-if="!whatsappInsights.loading">{{ whatsappInsights.todays_conversations || 0 }} today</span>
      </div>

      <!-- Show loading state -->
      <div v-if="whatsappInsights.loading" class="insights-loading">
        <div class="loading-spinner"></div>
        <p>Loading...</p>
      </div>

      <!-- Show error state -->
      <div v-else-if="whatsappInsights.error" class="insights-error">
        <div class="error-icon">⚠️</div>
        <p>{{ whatsappInsights.error }}</p>
      </div>

      <!-- Show WhatsApp stats -->
      <div v-else class="whatsapp-stats-grid">
        <div class="stat-card primary">
          <div class="stat-number">{{ whatsappInsights.todays_conversations || 0 }}</div>
          <div class="stat-label">Today</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ whatsappInsights.yesterdays_conversations || 0 }}</div>
          <div class="stat-label">Yesterday</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ whatsappInsights.week_conversations || 0 }}</div>
          <div class="stat-label">Week</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">{{ whatsappInsights.month_conversations || 0 }}</div>
          <div class="stat-label">Month</div>
        </div>
      </div>

      <!-- Change indicator -->
      <div class="change-summary" v-if="whatsappInsights.change_from_yesterday">
        <div class="change-indicator" :class="whatsappInsights.change_from_yesterday?.absolute >= 0 ? 'positive' : 'negative'">
          <span class="change-icon">{{ whatsappInsights.change_from_yesterday?.absolute >= 0 ? '📈' : '📉' }}</span>
          <span class="change-text">
            {{ whatsappInsights.change_from_yesterday?.absolute >= 0 ? '+' : '' }}{{ whatsappInsights.change_from_yesterday?.absolute || 0 }} vs yesterday
          </span>
          <span class="change-percent">{{ whatsappInsights.change_from_yesterday?.percentage?.toFixed(1) || 0 }}%</span>
        </div>
      </div>
    </div>

    <!-- Recent Activity -->
    <div class="recent-activity-section">
      <h2>Recent Activity</h2>
      <div class="activity-list">
        <div v-if="recentActivity.length === 0" class="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
            <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm-2 14l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
          </svg>
          <p>No recent activity</p>
        </div>

        <div v-for="activity in recentActivity" :key="activity.id" class="activity-item">
          <div :class="['activity-icon', activity.type]">
            <svg v-if="activity.type === 'lead'" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
            <svg v-if="activity.type === 'message'" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
            </svg>
            <svg v-if="activity.type === 'completed'" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          </div>
          <div class="activity-content">
            <div class="activity-title">{{ activity.title }}</div>
            <div class="activity-time">{{ activity.time }}</div>
          </div>
        </div>
      </div>
    </div>
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import { authStore } from '@/store/auth'

export default {
  name: 'AgentDashboardView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      agentName: '',
      departmentName: '',
      roleName: '',
      authStore,
      leadData: {
        todayStats: null,
        weekStats: null,
        monthStats: null,
        allTimeStats: null,
        recentCompletions: [],
        motivationalMessage: null,
        loading: true,
        error: null
      },
      whatsappInsights: {
        todays_conversations: 0,
        yesterdays_conversations: 0,
        week_conversations: 0,
        month_conversations: 0,
        change_from_yesterday: null,
        loading: true,
        error: null
      },
      recentActivity: [],
      isRefreshing: false
    }
  },
  async mounted () {
    // Check authentication
    const agentToken = localStorage.getItem('agent_token')
    if (!agentToken) {
      this.$router.push('/agent-login')
      return
    }

    // Load agent data
    const agentData = JSON.parse(localStorage.getItem('agent_data') || '{}')
    this.agentName = agentData.name || 'Agent'
    this.departmentName = agentData.department_name || 'Department'
    this.roleName = agentData.agent_role_name || 'Role'

    // Load dashboard data
    await this.checkAppointmentMode()
    await this.loadDashboardData()
    await this.loadLeadStats()
    await this.loadWhatsAppStats()
  },
  methods: {
    async checkAppointmentMode () {
      const agentToken = localStorage.getItem('agent_token')

      try {
        const response = await fetch('https://api.quali-d.com/appointments/mode/check', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${agentToken}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const result = await response.json()
          console.log('Appointment mode check result:', result)

          // Update auth store with appointment mode status
          // Check both possible property names for compatibility
          if (result.appointment_mode_enabled || result.appointment_mode) {
            this.authStore.setAppointmentMode(true)
            console.log('Appointment mode enabled for agent')
          } else {
            this.authStore.setAppointmentMode(false)
            console.log('Appointment mode disabled for agent')
          }
        }
      } catch (error) {
        console.error('Error checking appointment mode:', error)
        this.authStore.setAppointmentMode(false)
      }
    },

    async loadDashboardData () {
      const agentToken = localStorage.getItem('agent_token')

      try {
        // Verify token is still valid
        const profileResponse = await fetch('https://api.quali-d.com/agents/me', {
          headers: {
            Authorization: `Bearer ${agentToken}`,
            Accept: 'application/json'
          }
        })

        if (!profileResponse.ok) {
          // Token invalid, redirect to login
          localStorage.removeItem('agent_token')
          localStorage.removeItem('agent_data')
          this.$router.push('/agent-login')
          return
        }

        // Load recent activity from API
        const activityResponse = await fetch('https://api.quali-d.com/agents/activity', {
          headers: {
            Authorization: `Bearer ${agentToken}`,
            Accept: 'application/json'
          }
        })

        if (activityResponse.ok) {
          const activityData = await activityResponse.json()
          this.recentActivity = activityData.activities || []
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error)
        this.recentActivity = []
      }
    },

    async refreshData () {
      this.isRefreshing = true
      await this.checkAppointmentMode()
      await this.loadDashboardData()
      await this.loadLeadStats()
      await this.loadWhatsAppStats()
      setTimeout(() => {
        this.isRefreshing = false
      }, 500)
    },

    async loadLeadStats () {
      try {
        this.leadData.loading = true
        this.leadData.error = null
        const agentToken = localStorage.getItem('agent_token')

        const response = await fetch('https://api.quali-d.com/collected-leads/dashboard-stats', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${agentToken}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        this.leadData.todayStats = data.todayStats
        this.leadData.weekStats = data.weekStats
        this.leadData.monthStats = data.monthStats
        this.leadData.allTimeStats = data.allTimeStats
        this.leadData.recentCompletions = data.recentCompletions || []
        this.leadData.motivationalMessage = data.motivationalMessage
      } catch (error) {
        console.error('Error loading lead stats:', error)
        this.leadData.error = error.message

        // Set fallback data
        this.leadData.todayStats = { count: 0, trend: '+0' }
        this.leadData.weekStats = { count: 0, trend: '+0' }
        this.leadData.monthStats = { count: 0, trend: '+0' }
        this.leadData.allTimeStats = { count: 0, trend: '+0' }
        this.leadData.recentCompletions = []
        this.leadData.motivationalMessage = 'Keep up the great work! 💪'
      } finally {
        this.leadData.loading = false
      }
    },

    getTrendClass (trend) {
      if (!trend) return 'neutral'
      const value = parseInt(trend.replace(/[^-\d]/g, ''))
      return value > 0 ? 'positive' : value < 0 ? 'negative' : 'neutral'
    },

    formatTime (timestamp) {
      if (!timestamp) return 'Unknown'
      const date = new Date(timestamp)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMins / 60)
      const diffDays = Math.floor(diffHours / 24)

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      return `${diffDays}d ago`
    },

    async loadWhatsAppStats () {
      try {
        this.whatsappInsights.loading = true
        this.whatsappInsights.error = null
        const agentToken = localStorage.getItem('agent_token')

        const response = await fetch('https://api.quali-d.com/todays-conversations', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${agentToken}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        this.whatsappInsights.todays_conversations = data.todays_conversations || 0
        this.whatsappInsights.yesterdays_conversations = data.yesterdays_conversations || 0
        this.whatsappInsights.week_conversations = data.week_conversations || 0
        this.whatsappInsights.month_conversations = data.month_conversations || 0
        this.whatsappInsights.change_from_yesterday = data.change_from_yesterday || null
      } catch (error) {
        console.error('Error loading WhatsApp stats:', error)
        this.whatsappInsights.error = error.message

        // Set fallback data
        this.whatsappInsights.todays_conversations = 0
        this.whatsappInsights.yesterdays_conversations = 0
        this.whatsappInsights.week_conversations = 0
        this.whatsappInsights.month_conversations = 0
        this.whatsappInsights.change_from_yesterday = null
      } finally {
        this.whatsappInsights.loading = false
      }
    },

    async handleLogout () {
      const agentToken = localStorage.getItem('agent_token')

      try {
        await fetch('https://api.quali-d.com/agents/logout', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${agentToken}`,
            Accept: 'application/json'
          }
        })
      } catch (error) {
        console.error('Logout error:', error)
      } finally {
        // Clear local storage and redirect
        localStorage.removeItem('agent_token')
        localStorage.removeItem('agent_data')
        this.$router.push('/agent-login')
      }
    }
  }
}
</script>

<style scoped>
.agent-dashboard-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-primary);
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 280px;
  min-height: 100vh;
}

.page-content {
  flex: 1;
  padding: 24px;
  background: var(--bg-secondary);
}

/* Welcome Header */
.welcome-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.welcome-section h1 {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.refresh-button,
.logout-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.refresh-button:hover:not(:disabled),
.logout-button:hover {
  background: var(--bg-secondary);
  border-color: var(--border-secondary);
}

.refresh-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.refresh-button svg.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Quick Actions */
.quick-actions-section {
  margin-bottom: 32px;
}

.quick-actions-section h2 {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 16px 0;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.action-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  text-decoration: none;
  box-shadow: 0 1px 3px var(--shadow-primary);
  transition: all 0.2s;
}

.action-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px var(--shadow-secondary);
}

.action-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.action-content {
  flex: 1;
}

.action-content h3 {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.action-content p {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.action-arrow {
  color: var(--text-tertiary);
  flex-shrink: 0;
}

/* Recent Activity */
.recent-activity-section {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--shadow-primary);
}

.recent-activity-section h2 {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 20px 0;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.empty-state {
  text-align: center;
  padding: 48px 24px;
  color: var(--text-tertiary);
}

.empty-state p {
  margin: 12px 0 0 0;
  font-size: 14px;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  border-radius: 8px;
  transition: background 0.2s;
}

.activity-item:hover {
  background: var(--bg-secondary);
}

.activity-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.activity-icon.lead {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.activity-icon.message {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.activity-icon.completed {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.activity-content {
  flex: 1;
}

.activity-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.activity-time {
  font-size: 13px;
  color: var(--text-tertiary);
}

/* Insight Cards - Matching DashboardLayout.vue */
.insight-card {
  background: var(--bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  padding: 24px;
  margin-bottom: 32px;
  transition: all 0.3s ease;
}

.insight-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px var(--shadow-primary);
  border-color: var(--border-secondary);
}

.insight-card.lead-insights {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
}

.insight-card.lead-insights .insight-title,
.insight-card.lead-insights .insight-count {
  color: white;
}

.insight-card.whatsapp-insights {
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  border: none;
  color: white;
}

.insight-card.whatsapp-insights .insight-title,
.insight-card.whatsapp-insights .insight-count {
  color: white;
}

.insight-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.insight-title-with-icon {
  display: flex;
  align-items: center;
  gap: 12px;
}

.insight-title-content {
  flex: 1;
}

.insight-title-content .insight-title {
  margin: 0 0 2px;
  font-size: 16px;
  font-weight: 600;
}

.insight-title-content .insight-subtitle {
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  opacity: 0.8;
}

.insight-count {
  font-size: 14px;
  font-weight: 600;
  opacity: 0.9;
}

.lead-intelligence-icon,
.whatsapp-icon {
  color: white;
  flex-shrink: 0;
}

/* Lead Stats Grid */
.lead-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  transition: all 0.3s ease;
}

.stat-card:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
}

.stat-card.primary {
  background: rgba(255, 255, 255, 0.2);
}

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

.stat-rate {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 2px;
}

/* Recent Activity (inside cards) */
.recent-activity {
  margin-top: 20px;
}

.activity-title {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 12px;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.activity-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #10b981;
  flex-shrink: 0;
}

.activity-info {
  flex: 1;
  min-width: 0;
}

.activity-text {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.activity-time {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 2px;
}

/* Motivational Message */
.motivational-message {
  margin-top: 20px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.motivational-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.motivational-text {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 500;
  text-align: center;
}

/* WhatsApp Stats Grid */
.whatsapp-stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

/* Change Summary */
.change-summary {
  margin-top: 16px;
}

.change-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.change-indicator.positive {
  background: rgba(16, 185, 129, 0.2);
}

.change-indicator.negative {
  background: rgba(239, 68, 68, 0.2);
}

.change-icon {
  font-size: 16px;
}

.change-text {
  flex: 1;
  font-size: 12px;
  color: white;
  font-weight: 500;
}

.change-percent {
  font-size: 12px;
  font-weight: 600;
  color: white;
}

/* Loading and Error States */
.insights-loading,
.insights-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
}

.insights-loading .loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

.error-icon {
  font-size: 32px;
  margin-bottom: 8px;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
  }

  .page-content {
    padding: 16px;
  }

  .welcome-section h1 {
    font-size: 24px;
  }

  .actions-grid {
    grid-template-columns: 1fr;
  }

  .insight-card {
    padding: 16px;
  }

  .lead-stats-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .whatsapp-stats-grid {
    grid-template-columns: 1fr;
  }
}
</style>
